import ButtonT from '@components/tailwind-components/Button/ButtonT';
import { Modal } from '@components/tailwind-components/Modal/Modal';
import type { ArticleArchitectTopdeskGenerationEstimateData } from '@conversed/shared/src/central/dto/article-architect.dto';
import type React from 'react';
import { useTranslation } from 'react-i18next';

// * EUR per generated word behind the backfill budget ceiling
// * (BACKFILL_WEEKLY_BUDGET_CEILING_WORDS = round(999 / 0.00026)). This is the
// * client invoice rate, not our own spend. No shared front-end constant exists
// * yet - see the report note to promote it. The billed word count is the whole
// * exchange (prompt input plus the model response), so the estimate adds the
// * projected output words to the input-only dry-run total.
const COST_PER_WORD_EUR = 0.00026;

// * AWS Bedrock Sonnet list price (USD per million tokens) plus the word-to-token
// * and USD-to-EUR factors used to turn the word estimate into our own spend. The
// * result is an estimate, not a billed figure: the dry-run word total counts the
// * prompt input only, so the output tokens are assumed from the article count.
const AWS_USD_PER_MILLION_INPUT_TOKENS = 3;
const AWS_USD_PER_MILLION_OUTPUT_TOKENS = 15;
const USD_TO_EUR = 1.08;
const INPUT_TOKENS_PER_WORD = 2.2;
const OUTPUT_TOKENS_PER_WORD = 1.4;
const OUTPUT_WORDS_PER_ARTICLE = 400;

const getAwsCostEur = (wordTotal: number, articleCount: number): number => {
  const inputTokens = wordTotal * INPUT_TOKENS_PER_WORD;
  const outputTokens =
    articleCount * OUTPUT_WORDS_PER_ARTICLE * OUTPUT_TOKENS_PER_WORD;
  const usd =
    (inputTokens / 1_000_000) * AWS_USD_PER_MILLION_INPUT_TOKENS +
    (outputTokens / 1_000_000) * AWS_USD_PER_MILLION_OUTPUT_TOKENS;

  return usd / USD_TO_EUR;
};

const getClientCostEur = (wordTotal: number, articleCount: number): number => {
  const billedWords = wordTotal + articleCount * OUTPUT_WORDS_PER_ARTICLE;

  return billedWords * COST_PER_WORD_EUR;
};

interface TopdeskGenerationEstimateModalProps {
  estimate: ArticleArchitectTopdeskGenerationEstimateData;
  onConfirm: () => void;
  onCancel: () => void;
}

export const TopdeskGenerationEstimateModal: React.FC<
  TopdeskGenerationEstimateModalProps
> = ({ estimate, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const clientCostEur = getClientCostEur(
    estimate.wordTotal,
    estimate.articleCount,
  );
  const awsCostEur = getAwsCostEur(estimate.wordTotal, estimate.articleCount);

  return (
    <Modal
      isOpen
      onClose={onCancel}
      size='sm'
      className='p-6'
      header={t('article-architect.topdesk-generation.modal.title')}>
      <p className='text-tiny text-gray-500 mb-4'>
        {t('article-architect.topdesk-generation.modal.description')}
      </p>
      <div className='flex flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3'>
        <div className='flex items-center justify-between text-tiny text-gray-700'>
          <span>
            {t('article-architect.topdesk-generation.modal.cluster-count')}
          </span>
          <span className='font-medium'>
            {estimate.clusterCount.toLocaleString()}
          </span>
        </div>
        <div className='flex items-center justify-between text-tiny text-gray-700'>
          <span>
            {t('article-architect.topdesk-generation.modal.article-count')}
          </span>
          <span className='font-medium'>
            {estimate.articleCount.toLocaleString()}
          </span>
        </div>
        <div className='flex items-center justify-between text-tiny text-gray-700'>
          <span>
            {t('article-architect.topdesk-generation.modal.word-total')}
          </span>
          <span className='font-medium'>
            {estimate.wordTotal.toLocaleString()}
          </span>
        </div>
      </div>

      <div className='mt-3 flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3'>
        <div className='flex flex-col gap-0.5'>
          <div className='flex items-center justify-between'>
            <span className='text-tiny font-medium text-gray-800'>
              {t('article-architect.topdesk-generation.modal.cost-client')}
            </span>
            <span className='text-base font-semibold text-gray-900'>
              {t('article-architect.topdesk-generation.modal.cost-amount', {
                amount: clientCostEur.toFixed(2),
              })}
            </span>
          </div>
          <span className='text-tiny text-gray-500'>
            {t('article-architect.topdesk-generation.modal.cost-client-hint')}
          </span>
        </div>
        <div className='flex flex-col gap-0.5 border-t border-gray-200 pt-3'>
          <div className='flex items-center justify-between'>
            <span className='text-tiny font-medium text-gray-800'>
              {t('article-architect.topdesk-generation.modal.cost-aws')}
            </span>
            <span className='text-base font-semibold text-gray-900'>
              {t('article-architect.topdesk-generation.modal.cost-amount', {
                amount: awsCostEur.toFixed(2),
              })}
            </span>
          </div>
          <span className='text-tiny text-gray-500'>
            {t('article-architect.topdesk-generation.modal.cost-aws-hint')}
          </span>
        </div>
      </div>
      <div className='flex justify-end gap-2 mt-6'>
        <ButtonT
          type='button'
          styling='cancel'
          rounding='md'
          size='xs'
          onClick={onCancel}>
          {t('article-architect.topdesk-generation.modal.cancel')}
        </ButtonT>
        <ButtonT
          type='button'
          styling='primary'
          rounding='md'
          size='xs'
          onClick={onConfirm}>
          {t('article-architect.topdesk-generation.modal.confirm')}
        </ButtonT>
      </div>
    </Modal>
  );
};
